import { useState, useRef, useEffect } from 'react';

/**
 * Reconnecting WebSocket con token siempre fresco.
 *
 * Recibe una función async `resolveUrl()` que devuelve la URL de conexión
 * (con un token JWT VÁLIDO) o `null` para no conectar todavía. Se invoca en
 * CADA intento de (re)conexión, así un access token caducado se refresca
 * ANTES de reconectar. Esto evita el bucle infinito de 401 en `$connect`
 * cuando el token de 1h expira en sesiones de larga vida (tablets, pestañas
 * inactivas): el REST ya refrescaba el token vía interceptor de axios, pero
 * el WebSocket horneaba el token una sola vez y reconectaba con él muerto.
 *
 * @param {() => Promise<string|null>} resolveUrl  Resolver de URL con token fresco.
 * @param {boolean} enabled  Si false, no se intenta conectar.
 */
export default function useReconnectingWebSocket(resolveUrl, enabled = true) {
  const [socket, setSocket] = useState(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  // Mantiene el último resolver sin re-disparar el efecto en cada render.
  const resolveRef = useRef(resolveUrl);
  resolveRef.current = resolveUrl;

  useEffect(() => {
    if (!enabled) return undefined;
    cancelledRef.current = false;
    let ws = null;

    function scheduleRetry() {
      const delay = Math.min(30000, 1000 * 2 ** retryRef.current++);
      timerRef.current = setTimeout(connect, delay);
    }

    async function connect() {
      if (cancelledRef.current) return;
      let url = null;
      try {
        url = await resolveRef.current();
      } catch (_) {
        url = null;
      }
      if (cancelledRef.current) return;
      if (!url) {
        // Sin token utilizable todavía: reintenta con backoff (no spamea).
        scheduleRetry();
        return;
      }
      ws = new WebSocket(url);
      ws.onopen = () => {
        retryRef.current = 0;
        setSocket(ws);
      };
      ws.onclose = () => {
        setSocket(null);
        if (cancelledRef.current) return;
        scheduleRetry();
      };
      ws.onerror = () => ws.close();
    }

    connect();
    return () => {
      cancelledRef.current = true;
      clearTimeout(timerRef.current);
      ws?.close();
    };
  }, [enabled]);

  const safeSend = (data) =>
    socket?.readyState === WebSocket.OPEN && socket.send(JSON.stringify(data));

  return { socket, safeSend };
}
